import { esriQuery, esriIdentifyImageService } from 'js/utils/esriQueries';
import { query_state_zone } from 'js/loads/other';
import { Promise as bbPromise } from 'promise';
import standardsConfig from '../standards.config';

function query_hurricane_wind_debris(geometry, standardsVersion = '7-10') {
  const url = standardsConfig[standardsVersion].windDebrisUrl;
  const options = {
    geometry,
    outFields: ['*'],
  };
  return esriQuery(url, options);
}

function query_hurricane_wind_debris_1_mile(geometry) {
  const url = 'https://gis.asce.org/arcgis/rest/services/ASCE/ASCE_Hurricane_WindBorneDebris/MapServer/4';
  const options = {
    geometry,
    outFields: ['*'],
  };
  return esriQuery(url, options);
}

function identify_wind_reoccurence(geometry, load, standardsVersion) {
  let endpoints;
  if (standardsVersion === '7-22') {
    endpoints = standardsConfig[standardsVersion].windServices.reoccurences;
  } else {
    endpoints = load.layers.queryDictionary.windReoccurence[standardsVersion];
  }
  const options = { geometry };
  return Object.values(endpoints).map((endpoint) => {
    return esriIdentifyImageService(endpoint, options);
  });
}

function identify_special_wind_region_mosaic_datasets(geometry, load, standardsVersion, riskLevel) {
  if (standardsVersion === '7-22') {
    const endpoints = standardsConfig[standardsVersion].windServices.specialWindRegionMosaicDatasets;
    const options = { geometry };

    return endpoints.map((endpoint) => {
      return esriIdentifyImageService(endpoint, options);
    });
  }
}

function identify_special_wind_zone(geometry, load, standardsVersion) {
  let url;
  switch (standardsVersion) {
    case '7-10':
      url = load.layers.queryDictionary.specialZoneURL2010;
      break;
    case '7-16':
      url = load.layers.queryDictionary.specialZoneURL2016;
      break;
    case '7-22':
      url = standardsConfig[standardsVersion].windServices.specialWindRegions;
  }
  const options = {
    geometry,
    outFields: ['*'],
  };
  return esriQuery(url, options);
}

function identify_wind(geometry, load, standardsVersion, riskLevel, isPRGeometry) {
  let url;
  const options = { geometry };

  if (standardsVersion === '7-22') {
    if (isPRGeometry) {
      url = standardsConfig[standardsVersion].windServices.windServicePRDataset;
    } else {
      url = standardsConfig[standardsVersion].windServices.windServiceDataset;
    }
  } else {
    url = load.layers.queryDictionary[standardsVersion][riskLevel];
  }

  return esriIdentifyImageService(url, options);
}

export function windQuery(geometry, load, riskLevel, standardsVersion, isPRDataset) {
  return bbPromise.all([
    query_hurricane_wind_debris(geometry, standardsVersion),
    query_hurricane_wind_debris_1_mile(geometry, standardsVersion),
    identify_wind(geometry, load, standardsVersion, riskLevel, isPRDataset),
    identify_special_wind_zone(geometry, load, standardsVersion),
    identify_special_wind_region_mosaic_datasets(geometry, load, standardsVersion, riskLevel),
    query_state_zone(geometry, load),
    ...identify_wind_reoccurence(geometry, load, standardsVersion),
  ]);
}

export function determineWindDataSource(standardsVersion, riskLevel, state) {
  if (standardsVersion === '7-10') {
    switch (riskLevel) {
      case 1:
        return 'ASCE/SEI 7-10, Fig. 26.5-1C and Figs. CC-1–CC-4, and Section 26.5.2, incorporating errata of March 12, 2014';
      case 2:
        return 'ASCE/SEI 7-10, Fig. 26.5-1A and Figs. CC-1–CC-4, and Section 26.5.2, incorporating errata of March 12, 2014';
      case 3:
      case 4:
        return 'ASCE/SEI 7-10, Fig. 26.5-1B and Figs. CC-1–CC-4, and Section 26.5.2, incorporating errata of March 12, 2014';
    }
  } else {
    if (state === 'Hawaii') {
      switch (riskLevel) {
        case 1:
          return `ASCE/SEI ${standardsVersion}, Fig. 26.5-2A and Figs. CC.2-1–CC.2-4, and Section 26.5.2`;
        case 2:
          return `ASCE/SEI ${standardsVersion}, Fig. 26.5-2B and Figs. CC.2-1–CC.2-4, and Section 26.5.2`;
        case 3:
          return `ASCE/SEI ${standardsVersion}, Fig. 26.5-2C and Figs. CC.2-1–CC.2-4, and Section 26.5.2`;
        case 4:
          return `ASCE/SEI ${standardsVersion}, Fig. 26.5-2D and Figs. CC.2-1–CC.2-4, and Section 26.5.2`;
      }
    } else {
      switch (riskLevel) {
        case 1:
          return `ASCE/SEI ${standardsVersion}, Fig. 26.5-1A and Figs. CC.2-1–CC.2-4, and Section 26.5.2`;
        case 2:
          return `ASCE/SEI ${standardsVersion}, Fig. 26.5-1B and Figs. CC.2-1–CC.2-4, and Section 26.5.2`;
        case 3:
          return `ASCE/SEI ${standardsVersion}, Fig. 26.5-1C and Figs. CC.2-1–CC.2-4, and Section 26.5.2`;
        case 4:
          return `ASCE/SEI ${standardsVersion}, Fig. 26.5-1D and Figs. CC.2-1–CC.2-4, and Section 26.5.2`;
      }
    }
  }
}

export function determineWindNote(riskLevel, standardsVersion, measurement, state) {
  let windNote = `Value provided is 3-second gust wind speeds at ${
    measurement === 'si' ? '10 m' : '33 ft'
  } above ground for Exposure C Category, based on linear interpolation between contours. Wind speeds are interpolated in accordance with the ${standardsVersion} Standard.`;
  switch (riskLevel) {
    case 1:
      windNote +=
        ' Wind speeds correspond to approximately a 15% probability of exceedance in 50 years (annual exceedance probability = 0.00333, MRI = 300 years).';
      break;
    case 2:
      windNote +=
        ' Wind speeds correspond to approximately a 7% probability of exceedance in 50 years (annual exceedance probability = 0.00143, MRI = 700 years).';
      break;
    case 3:
      windNote +=
        ' Wind speeds correspond to approximately a 3% probability of exceedance in 50 years (annual exceedance probability = 0.000588, MRI = 1,700 years).';
      break;
    case 4:
      if (standardsVersion === '7-10') {
        windNote +=
          ' Wind speeds correspond to approximately a 3% probability of exceedance in 50 years (annual exceedance probability = 0.000588, MRI = 1,700 years).';
      } else {
        if (state === 'Hawaii') {
          windNote +=
            ' Wind speeds correspond to approximately a 1.7% probability of exceedance in 50 years (annual exceedance probability = 0.00033, MRI = 3,000 years).';
        } else {
          windNote +=
            ' Wind speeds correspond to approximately a 1.6% probability of exceedance in 50 years (annual exceedance probability = 0.00033, MRI = 3,000 years).';
        }
      }
      break;
  }
  if (standardsVersion === '7-22') {
    windNote +=
      ' Values for 10-year MRI, 25-year MRI, 50-year MRI and 100-year MRI are Service Level wind speeds, all other wind speeds are Ultimate wind speeds.';
  }
  return windNote;
}

export function determineWindDebrisNote(
  riskLevel,
  standardsVersion,
  isWithinHurricaneZoneBoundary,
  isWithinMile,
  windSpeed,
  geometry,
  load
) {
  return new bbPromise((resolve, reject) => {
    // console.table(
    //     [
    //         ['Risk level', riskLevel],
    //         ['Standards version', standardsVersion],
    //         ['Is within boundary?', isWithinBoundary],
    //         ['Is within mile?', isWithinMile],
    //         ['Wind speed', windSpeed]
    //     ]
    // );
    const is710 = standardsVersion === '7-10';
    const is716 = standardsVersion === '7-16';
    const is722 = standardsVersion === '7-22';

    const windDebrisNotes = {
      A: `Site is not in a hurricane-prone region as defined in ASCE/SEI ${standardsVersion} Section 26.2.`,
      B: `Site is in a hurricane-prone region as defined in ASCE/SEI ${standardsVersion} Section 26.2. Glazed openings need not be protected against wind-borne debris.`,
      C: `Site is in a hurricane-prone region as defined in ASCE/SEI ${standardsVersion} Section 26.2. Glazed openings in health-care facilities shall be protected against wind-borne debris as specified in Section 26.${
        is710 ? '10' : is716 || is722 ? '12' : ''
      }.3.`,
      D: `Site is in a hurricane-prone region as defined in ASCE/SEI ${standardsVersion} Section 26.2. Glazed openings shall be protected against wind-borne debris as specified in Section 26.${
        is710 ? '10' : is716 || is722 ? '12' : ''
      }.3.`,
    };

    let note;
    switch (riskLevel) {
      case 1:
        if (isWithinHurricaneZoneBoundary) {
          note = windDebrisNotes['B'];
        } else {
          note = windDebrisNotes['A'];
        }
        resolve(note);
        break;
      case 2:
        if (windSpeed >= 140) {
          note = windDebrisNotes['D'];
        } else if (windSpeed >= 130 && isWithinMile) {
          note = windDebrisNotes['D'];
        } else if (isWithinHurricaneZoneBoundary) {
          note = windDebrisNotes['B'];
        } else {
          note = windDebrisNotes['A'];
        }
        resolve(note);
        break;
      case 3:
        /** refer to assembla ticket #485
         *  the above windSpeed is based on the risk III windspeed map
         *  however, this risk level is also dependent risk II windspeed map
         */
        identify_wind(geometry, load, standardsVersion, 2).then((response) => {
          const { value } = response;
          const windSpeedRiskLevelII = typeof value === 'string' ? parseFloat(value) : value;

          if (windSpeedRiskLevelII >= 140) {
            note = windDebrisNotes['D'];
          } else if (windSpeedRiskLevelII >= 130 && isWithinMile) {
            note = windDebrisNotes['D'];
          } else if (windSpeed >= 140) {
            note = windDebrisNotes['C'];
          } else if (windSpeed >= 130 && isWithinMile) {
            note = windDebrisNotes['C'];
          } else if (isWithinHurricaneZoneBoundary) {
            note = windDebrisNotes['B'];
          } else {
            note = windDebrisNotes['A'];
          }

          resolve(note);
        });
        break;
      case 4:
        if (windSpeed >= 140) {
          note = windDebrisNotes['D'];
        } else if (windSpeed >= 130 && isWithinMile) {
          note = windDebrisNotes['D'];
        } else if (isWithinHurricaneZoneBoundary) {
          note = windDebrisNotes['B'];
        } else {
          note = windDebrisNotes['A'];
        }
        resolve(note);
        break;
    }
  });
}
